// The wallet's and the top-ups' server state. register-domain will read balanceQuery to say whether a
// name is affordable before asking (guide, rule 25), so this is the feature's public surface. A
// top-up that lands moves the balance, so whatever changes one invalidates billingKeys.all.
import { queryOptions } from "@tanstack/react-query";
import {
  getBalance,
  getFakePayment,
  getTopUpMethods,
  listTopUps,
  listWalletEntries,
  type EntryFilters,
  type TopUpFilters,
} from "./api";

export const billingKeys = {
  all: ["billing"] as const,
  balance: () => [...billingKeys.all, "balance"] as const,
  entries: () => [...billingKeys.all, "entries"] as const,
  entryPage: (page: number, filters: EntryFilters) => [...billingKeys.entries(), page, filters] as const,
  topUps: () => [...billingKeys.all, "top-ups"] as const,
  topUpPage: (page: number, filters: TopUpFilters) => [...billingKeys.topUps(), page, filters] as const,
  topUpMethods: () => [...billingKeys.all, "top-up-methods"] as const,
  fakePayment: (reference: string) => [...billingKeys.all, "fake-payment", reference] as const,
};

export const balanceQuery = () =>
  queryOptions({ queryKey: billingKeys.balance(), queryFn: async () => (await getBalance()).data });

/** One page of the billing history, newest first, with the page meta. */
export const walletEntriesQuery = (page: number, filters: EntryFilters) =>
  queryOptions({ queryKey: billingKeys.entryPage(page, filters), queryFn: () => listWalletEntries(page, filters) });

/** The gateway's channels with their fees, and the limits: no channel means no top-up (T2). */
export const topUpMethodsQuery = () =>
  queryOptions({ queryKey: billingKeys.topUpMethods(), queryFn: async () => (await getTopUpMethods()).data });

/** One page of the reseller's top-ups, newest first, narrowed by state and WIB days. */
export const topUpsQuery = (page: number, filters: TopUpFilters = {}) =>
  queryOptions({ queryKey: billingKeys.topUpPage(page, filters), queryFn: () => listTopUps(page, filters) });

/** The fake gateway's session behind a pay link; a 404 is an answer, not a retry. */
export const fakePaymentQuery = (reference: string) =>
  queryOptions({
    queryKey: billingKeys.fakePayment(reference),
    queryFn: async () => (await getFakePayment(reference)).data,
    retry: false,
  });
